import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import VideocamIcon from '@mui/icons-material/Videocam';

import { extractErrorMessage } from '../../api/errors';
import { getDebriefSetup, type DebriefSetup, type DebriefStatus } from '../../api/debriefs';
import { useDebriefRecorder } from './DebriefRecorder';

const STATUS_LABEL: Record<DebriefStatus, string> = {
  recording: 'Recording',
  assembling: 'Being put together',
  ready: 'Ready',
  failed: 'Failed',
};

/**
 * Record a debrief of this session (DEBRIEF-RECORDING): pick the cameras —
 * the ones that recorded the session are chosen — and start. The cameras
 * record on the edge appliance, this laptop records the sound; recording
 * then carries on in a small bar while the operator reviews the session.
 */
export function RecordDebriefButton({
  tenantId,
  sessionRowId,
  label,
  onRecorded,
}: {
  tenantId: string;
  sessionRowId: string;
  /** Whose debrief — shown while recording. */
  label: string;
  /** After a debrief was handed over, so the coaching list can refresh. */
  onRecorded?: () => void;
}) {
  const recorder = useDebriefRecorder();
  const [open, setOpen] = useState(false);
  const [setup, setSetup] = useState<DebriefSetup | null>(null);
  const [applianceId, setApplianceId] = useState('');
  const [cameraIds, setCameraIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setSetup(null);
    setError(null);
    getDebriefSetup(tenantId, sessionRowId)
      .then((s) => {
        if (!alive) return;
        setSetup(s);
        setApplianceId(s.default_appliance_id ?? s.appliances[0]?.id ?? '');
        setCameraIds(s.default_camera_ids);
      })
      .catch((e) => alive && setError(extractErrorMessage(e, 'could not load the cameras')));
    return () => {
      alive = false;
    };
  }, [open, tenantId, sessionRowId]);

  const appliance = setup?.appliances.find((a) => a.id === applianceId) ?? null;
  if (!recorder) return null;
  const recordingHere = recorder.active?.sessionRowId === sessionRowId;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await recorder.start({
        tenantId, sessionRowId, applianceId, cameraIds, label, onStopped: onRecorded,
      });
      setOpen(false);
    } catch (e) {
      setError(extractErrorMessage(e, 'could not start the debrief'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<VideocamIcon />}
        onClick={() => setOpen(true)}
        disabled={!!recorder.active}
      >
        {recordingHere ? 'Recording debrief…' : 'Record debrief'}
      </Button>
      <Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Record a debrief</DialogTitle>
        <DialogContent>
          {!setup && !error && <CircularProgress size={24} />}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {setup && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                The cameras of the edge appliance record the picture, this laptop records the
                sound. You can keep reviewing the session while it records; stop it from the
                bar at the bottom of the screen. It stops by itself after an hour.
              </Typography>
              {setup.appliances.length === 0 ? (
                <Alert severity="info">
                  No edge appliance with cameras is set up for this organisation.
                </Alert>
              ) : (
                <>
                  <TextField
                    select
                    label="Edge appliance"
                    value={applianceId}
                    onChange={(e) => {
                      setApplianceId(e.target.value);
                      const a = setup.appliances.find((x) => x.id === e.target.value);
                      setCameraIds(a ? a.cameras.map((c) => c.id) : []);
                    }}
                    size="small"
                    fullWidth
                  >
                    {setup.appliances.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormGroup aria-label="Cameras">
                    {(appliance?.cameras ?? []).map((c) => (
                      <FormControlLabel
                        key={c.id}
                        control={
                          <Checkbox
                            checked={cameraIds.includes(c.id)}
                            onChange={(e) =>
                              setCameraIds((ids) =>
                                e.target.checked ? [...ids, c.id] : ids.filter((x) => x !== c.id),
                              )
                            }
                          />
                        }
                        label={c.name}
                      />
                    ))}
                  </FormGroup>
                </>
              )}
              <Alert severity="info" variant="outlined">
                If you play the recording with sound while you talk, use headphones or mute it —
                otherwise the laptop’s microphone picks it up too.
              </Alert>
              {setup.debriefs.length > 0 && (
                <Stack spacing={0.5}>
                  <Typography variant="overline" color="text.secondary">
                    Debriefs of this session
                  </Typography>
                  {setup.debriefs.map((d) => (
                    <Stack key={d.id} direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {new Date(d.started_at).toLocaleString()}
                      </Typography>
                      <Chip
                        size="small"
                        label={STATUS_LABEL[d.status]}
                        color={d.status === 'ready' ? 'success' : d.status === 'failed' ? 'error' : 'default'}
                      />
                      {d.error && (
                        <Typography variant="caption" color="text.secondary">
                          {d.error}
                        </Typography>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void start()}
            disabled={busy || !setup || !applianceId || cameraIds.length === 0}
          >
            {busy ? 'Starting…' : 'Start recording'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
